import { useAuthToken } from '@/hooks/useAuthToken';
import {
  failOpenPosthogWorkspaceFlagsSynced,
  syncWorkspaceIdToPosthogFlags,
} from '@/providers/posthog-workspace-flags';
import { useWorkspaceId } from '@heidi/workspace/data';
import { useEffect, useRef } from 'react';

/** Hydration timing supplied by the composition point that owns workspace boot. */
export type WorkspacePosthogSyncHydration = {
  /**
   * `awaiting_selection` is the post-login workspace selector holding the gate:
   * no workspace yet by design, so it is treated like `pending` rather than
   * failing the workspace-flag sync open.
   */
  state: 'pending' | 'awaiting_selection' | 'resolved' | 'degraded';
};

const isHydrationSettled = (state: WorkspacePosthogSyncHydration['state']) =>
  state !== 'pending' && state !== 'awaiting_selection';

/**
 * Publishes the active workspace_id to PostHog for eval-time flag targeting
 * and reloads flags when the atom changes.
 *
 * App-side only — PostHog cannot live in `@heidi/workspace/data`. Mounted from
 * `WorkspaceHydrationGate` (web) and `FhirAuth` (Haiku/EHR launch).
 */
export function useSyncWorkspaceIdToPosthog(
  hydration: WorkspacePosthogSyncHydration
): void {
  const token = useAuthToken();
  const workspaceId = useWorkspaceId();
  const lastPublishedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      lastPublishedIdRef.current = null;
      // Logout clears the token — do not fail-open over resetPosthogWorkspaceFlagsSynced.
      if (token && isHydrationSettled(hydration.state)) {
        failOpenPosthogWorkspaceFlagsSynced();
      }
      return;
    }
    if (workspaceId === lastPublishedIdRef.current) return;

    lastPublishedIdRef.current = workspaceId;
    syncWorkspaceIdToPosthogFlags(workspaceId);
  }, [workspaceId, hydration.state, token]);
}
